<template>
  <section class="page-stack">
    <article class="card page-hero-card">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">Roster Vault</p>
          <h2>Favorite characters</h2>
          <p>Your saved characters, profile refreshes and quick filtering by role, realm or score.</p>
        </div>
      </header>
    </article>

    <div class="card filter-toolbar">
      <div class="applied-filters">
        <span class="filter-pill">Saved: <span>{{ favorites.length }}</span></span>
        <span v-if="search" class="filter-pill">Search: <span>{{ search }}</span></span>
        <span class="filter-pill">Sort: <span>{{ sortBy }}</span></span>
      </div>
      <div role="group" class="quick-search-field">
        <input
          id="favorite-search"
          v-model.trim="search"
          class="input"
          aria-label="Search favorites"
          placeholder="Search by character name"
          type="search"
        />
      </div>
      <div class="page-actions">
        <div class="view-switch" aria-label="Display mode">
          <button
            class="btn-icon-outline"
            :class="{ 'is-active': viewMode === 'grid' }"
            type="button"
            aria-label="Cards"
            title="Cards"
            @click="viewMode = 'grid'"
          >
            <i class="fa-solid fa-grip"></i>
          </button>
          <button
            class="btn-icon-outline"
            :class="{ 'is-active': viewMode === 'list' }"
            type="button"
            aria-label="List"
            title="List"
            @click="viewMode = 'list'"
          >
            <i class="fa-solid fa-list"></i>
          </button>
        </div>
        <button class="btn-icon-outline" type="button" aria-label="Reset filters" title="Reset filters" @click="resetFilters">
          <i class="fa-solid fa-rotate-left"></i>
        </button>
        <button
          class="btn-icon-outline"
          type="button"
          :disabled="refreshingAll || !favorites.length"
          aria-label="Refresh all characters"
          title="Refresh all characters"
          @click="refreshAllFavorites(false)"
        >
          <i class="fa-solid fa-rotate"></i>
        </button>
        <button class="btn-icon" type="button" aria-label="Filters" title="Filters" @click="openFilters">
          <i class="fa-solid fa-sliders"></i>
        </button>
      </div>
    </div>

    <form class="card form-card" @submit.prevent="addByUrl">
      <div class="character-url-row">
        <div role="group" class="field character-url-field">
          <label class="label">Raider.io character URL</label>
          <input
            v-model.trim="characterUrl"
            class="input"
            placeholder="https://raider.io/characters/eu/howling-fjord/Name"
            type="url"
          />
        </div>

        <button class="btn-icon character-url-submit" type="submit" :disabled="loading" aria-label="Add character" title="Add character">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </form>

    <div
      v-if="isFiltersOpen"
      class="app-modal-backdrop filter-drawer-backdrop"
      :class="{ 'is-closing': closingFilters }"
      @click.self="closeFilters"
    >
      <aside class="app-filter-drawer" role="dialog" aria-modal="true" aria-labelledby="favorite-filters-title">
        <header>
          <h2 id="favorite-filters-title">Favorite filters</h2>
          <p>Search and sort saved characters.</p>
        </header>
        <section>
          <form class="form filters-grid" @submit.prevent="applyFilters">
            <div role="group" class="field filter-field">
              <label class="label">Sort by</label>
              <select v-model="draftSortBy" class="select">
                <option value="addedAt">Date added</option>
                <option value="score">M+ score</option>
                <option value="itemLevel">Item level</option>
                <option value="name">Name</option>
              </select>
            </div>
          </form>
        </section>
        <footer>
          <button class="btn-outline" type="button" @click="closeFilters">Cancel</button>
          <button class="btn" type="button" @click="applyFilters">Apply</button>
        </footer>
      </aside>
    </div>

    <AppEmptyState
      v-if="!favorites.length"
      eyebrow="Favorites"
      icon="fa-solid fa-star"
      title="No saved characters yet"
      message="Add a Raider.io character URL or save someone from Mythic+ runs to start building your roster vault."
      :actions="[
        { to: '/mythic-plus', label: 'Browse Mythic+', icon: 'fa-solid fa-dungeon' }
      ]"
    />

    <AppEmptyState
      v-else-if="!sortedFavorites.length"
      eyebrow="No matches"
      icon="fa-solid fa-magnifying-glass"
      title="Nothing matched these filters"
      message="Try a different character name, reset search, or change the sorting and filter options."
    />

    <div v-else :class="viewMode === 'list' ? 'favorites-list' : 'favorites-grid'">
      <article
        v-for="favorite in sortedFavorites"
        :key="favorite.id"
        class="card favorite-card"
        :style="{ '--favorite-accent': classColor(favorite.profile.class) || 'var(--primary)' }"
      >
        <button
          class="btn-icon-outline favorite-card-menu-button"
          type="button"
          aria-label="Open character menu"
          title="More actions"
          @click.stop="toggleFavoriteMenu(favorite.id)"
        >
          <i class="fa-solid fa-ellipsis-vertical"></i>
        </button>
        <Transition name="favorite-menu">
          <div v-if="activeMenuFavoriteId === favorite.id" class="favorite-context-menu" @click.stop>
            <button type="button" @click="refreshFavoriteFromMenu(favorite)">
              <i class="fa-solid fa-rotate"></i>
              <span>Refresh</span>
            </button>
            <button type="button" @click="addToCompareFromMenu(favorite)">
              <i class="fa-solid fa-code-compare"></i>
              <span>Add to compare</span>
            </button>
            <button type="button" @click="removeFavoriteFromMenu(favorite.id)">
              <i class="fa-solid fa-trash"></i>
              <span>Remove</span>
            </button>
          </div>
        </Transition>

        <header class="favorite-identity">
          <div class="favorite-avatar-wrap">
            <img
              v-if="favorite.profile.thumbnail_url"
              class="avatar-image"
              :src="favorite.profile.thumbnail_url"
              :alt="favorite.profile.name"
              loading="lazy"
            >
            <span class="faction-mark" :class="`is-${factionKey(favorite.profile.faction)}`" :title="favorite.profile.faction || 'Faction'">
              <img :src="factionIcon(favorite.profile.faction)" :alt="favorite.profile.faction || 'Faction'" loading="lazy">
            </span>
          </div>

          <div class="favorite-identity-copy">
            <h3>
              <img
                v-if="classIcon(favorite.profile.class)"
                class="favorite-class-icon"
                :src="classIcon(favorite.profile.class)"
                :alt="favorite.profile.class"
                loading="lazy"
              >
              <span class="favorite-name" :style="favoriteNameStyle(favorite.profile.class)">{{ favorite.profile.name }}</span>
            </h3>
            <p class="favorite-spec-line">{{ specLine(favorite.profile) }}</p>
            <p class="favorite-origin-line">
              <span v-if="favorite.profile.guild?.name" :title="`Guild: ${favorite.profile.guild.name}`">
                <i class="fa-solid fa-shield"></i> {{ favorite.profile.guild.name }}
              </span>
              <span :title="`Realm: ${favorite.profile.realm}`">
                <i class="fa-solid fa-server"></i> {{ favorite.profile.realm }} ({{ favorite.profile.region.toUpperCase() }})
              </span>
            </p>
          </div>
        </header>

        <div class="favorite-body">
          <dl class="favorite-headline">
            <div>
              <dd>{{ bestScore(favorite.profile) }}</dd>
              <dt>M+ score</dt>
            </div>
            <div>
              <dd>{{ itemLevel(favorite.profile) }}</dd>
              <dt>Item level</dt>
            </div>
          </dl>

          <dl class="favorite-subscores">
            <div>
              <dt>Raid</dt>
              <dd>{{ raidProgressLabel(favorite.profile) }}</dd>
            </div>
            <div>
              <dt>Realm</dt>
              <dd>{{ rankLabel(favorite.profile, 'realm') }}</dd>
            </div>
            <div>
              <dt>World</dt>
              <dd>{{ rankLabel(favorite.profile, 'world') }}</dd>
            </div>
          </dl>
        </div>

        <footer class="favorite-actions">
          <div class="favorite-loot is-items">
            <span class="favorite-loot-label">Key items</span>
            <div class="favorite-gear-row">
              <a
                v-for="item in keyGearItems(favorite.profile)"
                :key="`${favorite.id}-gear-${item.slotKey}`"
                class="mini-gear-icon"
                :class="{ 'is-empty': !item.item_id && !item.id }"
                :href="wowheadItemUrl(item)"
                :data-wowhead="wowheadItemData(item)"
                target="_blank"
                rel="noreferrer"
                :title="item.name"
              >
                <ItemIcon :item="item" :alt="item.name" loading="eager" />
                <b v-if="keyGearItemLevel(item)">{{ keyGearItemLevel(item) }}</b>
              </a>
            </div>
          </div>

          <div class="favorite-loot is-links">
            <span class="favorite-loot-label">Profiles</span>
            <div class="favorite-links">
              <a
                v-for="service in serviceLinks(favorite.profile)"
                :key="`${favorite.id}-${service.key}`"
                :href="service.href"
                target="_blank"
                rel="noreferrer"
                :aria-label="`Open ${service.label} profile`"
                :title="service.label"
              >
                <img :src="service.icon" :alt="service.label" loading="lazy">
              </a>
            </div>
          </div>

          <button
            class="btn-sm-outline favorite-armory-button"
            type="button"
            :aria-label="`Open ${favorite.profile.name} armory`"
            @click="openArmory(favorite)"
          >
            <i class="fa-solid fa-shield-halved" aria-hidden="true"></i>
            <span class="favorite-armory-copy">
              <strong>Armory</strong>
              <small>Gear &amp; runs</small>
            </span>
            <i class="fa-solid fa-chevron-right favorite-armory-chevron" aria-hidden="true"></i>
          </button>
        </footer>
      </article>
    </div>

    <div
      v-if="activeArmoryFavorite"
      class="app-modal-backdrop"
      :class="{ 'is-closing': closingArmory }"
      @click.self="closeArmory"
    >
      <section class="app-modal armory-modal" role="dialog" aria-modal="true" aria-labelledby="armory-modal-title">
        <header class="modal-head">
          <div>
            <p class="eyebrow">{{ activeArmoryTab.eyebrow }}</p>
            <h2 id="armory-modal-title">{{ activeArmoryFavorite.profile.name }}</h2>
          </div>
          <div class="armory-modal-actions">
            <div class="favorite-links armory-modal-links">
              <a
                v-for="service in serviceLinks(activeArmoryFavorite.profile)"
                :key="`armory-${service.key}`"
                :href="service.href"
                target="_blank"
                rel="noreferrer"
                :aria-label="`Open ${service.label} profile`"
                :title="service.label"
              >
                <img :src="service.icon" :alt="service.label" loading="lazy">
              </a>
            </div>
            <button class="btn-icon-outline" type="button" aria-label="Close character details" @click="closeArmory">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
        </header>

        <div class="armory-modal-switch view-switch" aria-label="Character section">
          <button
            v-for="tab in armoryTabs"
            :key="tab.key"
            class="btn-sm-outline"
            :class="{ 'is-active': armoryTab === tab.key }"
            type="button"
            @click="selectArmoryTab(tab.key)"
          >
            <i :class="tab.icon"></i> {{ tab.label }}
          </button>
        </div>

        <ArmoryProfilePanel
          v-if="activeArmoryTab.section"
          :key="`${activeArmoryFavorite.id}-${activeArmoryTab.section}`"
          :profile="activeArmoryFavorite.profile"
          :section="activeArmoryTab.section"
          @loaded="armoryPayload = $event"
        />

        <div v-else-if="armoryTab === 'runs'" class="armory-modal-body is-runs">
          <div class="runs-mode-switch view-switch" aria-label="Run list">
            <button
              class="btn-sm-outline"
              :class="{ 'is-active': runsMode === 'best' }"
              type="button"
              @click="runsMode = 'best'"
            >
              Best runs
            </button>
            <button
              class="btn-sm-outline"
              :class="{ 'is-active': runsMode === 'recent' }"
              type="button"
              @click="runsMode = 'recent'"
            >
              Recent runs
            </button>
          </div>

          <section v-if="runsMode === 'best'" class="best-runs-scroll">
            <div v-if="bestRuns(activeArmoryFavorite.profile).length" class="best-runs-grid">
              <a
                v-for="run in bestRuns(activeArmoryFavorite.profile)"
                :key="run.url || `${run.dungeon}-${run.mythic_level}`"
                class="best-run-card"
                :class="keystoneTierClass(run)"
                :href="run.url || activeArmoryFavorite.profile.profile_url"
                target="_blank"
                rel="noreferrer"
              >
                <span class="best-run-banner">
                  <img v-if="dungeonRunBanner(run)" :src="dungeonRunBanner(run)" :alt="dungeonRunName(run)" loading="eager">
                  <span class="best-run-keystone">
                    <b>+{{ run.mythic_level || '-' }}</b>
                  </span>
                  <span class="best-run-title">
                    <img v-if="dungeonRunIcon(run)" :src="dungeonRunIcon(run)" :alt="dungeonRunName(run)" loading="eager">
                    <strong>{{ dungeonRunName(run) }}</strong>
                  </span>
                </span>

                <span class="best-run-stats">
                  <span class="best-run-score">
                    <b>{{ formatScore(run.score) }}</b>
                    <small>score</small>
                  </span>
                  <span class="best-run-fact">
                    <b>{{ runTime(run) }}</b>
                    <small>{{ timeRemainingLabel(run) }}</small>
                  </span>
                  <span class="best-run-fact">
                    <b v-if="run.num_keystone_upgrades">+{{ run.num_keystone_upgrades }}</b>
                    <b v-else class="is-depleted">0</b>
                    <small>{{ chestLabel(run) }}</small>
                  </span>
                  <span class="best-run-fact">
                    <b>{{ runCompletedShort(run) }}</b>
                    <small>cleared</small>
                  </span>
                </span>
              </a>
            </div>
            <p v-else class="runs-empty">No best runs found for this character.</p>
          </section>

          <section v-else class="table-wrap">
            <table class="table favorite-runs-table">
              <thead>
                <tr>
                  <th>Dungeon</th>
                  <th>Level</th>
                  <th>Score</th>
                  <th>Time</th>
                  <th>Up</th>
                  <th>Completed</th>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="run in recentRuns(activeArmoryFavorite.profile)" :key="run.url || `${run.dungeon}-${run.mythic_level}-${run.completed_at}`">
                  <td class="dungeon-cell-compact">
                    <a :href="run.url || activeArmoryFavorite.profile.profile_url" target="_blank" rel="noreferrer">
                      <img v-if="dungeonRunIcon(run)" :src="dungeonRunIcon(run)" :alt="dungeonRunName(run)" loading="lazy">
                      <strong>{{ dungeonRunName(run) }}</strong>
                    </a>
                  </td>
                  <td class="rank-cell">{{ run.mythic_level || '-' }}</td>
                  <td class="score-cell">{{ formatScore(run.score) }}</td>
                  <td class="time-cell"><span>{{ runTime(run) }}</span></td>
                  <td>{{ upgradeStars(run) }}</td>
                  <td class="time-cell"><span>{{ runCompletedAt(run) }}</span></td>
                  <td>
                    <a v-if="run.url" class="ranking-link-icon" :href="run.url" target="_blank" rel="noreferrer" aria-label="Open run">
                      <i class="fa-solid fa-link"></i>
                    </a>
                    <span v-else>-</span>
                  </td>
                </tr>
                <tr v-if="!recentRuns(activeArmoryFavorite.profile).length" class="is-empty-row">
                  <td colspan="7">No recent runs found for this character.</td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>

        <div v-else-if="armoryTab === 'gear'" class="armory-modal-body is-gear">
          <section class="armory-frame" :class="{ 'has-render': !!armoryRender }">
            <div v-if="armoryRender" class="armory-render" aria-hidden="true">
              <img v-if="armoryRender.background" class="armory-render-background" :src="armoryRender.background" alt="" loading="eager">
              <img v-if="armoryRender.shadow" class="armory-render-shadow" :src="armoryRender.shadow" alt="" loading="eager">
              <img v-if="armoryRender.foreground" class="armory-render-figure" :src="armoryRender.foreground" alt="" loading="eager">
            </div>

            <GearLoadoutShowcase
              eyebrow="Equipped loadout"
              :title="activeArmoryFavorite.profile.active_spec_name || activeArmoryFavorite.profile.class || activeArmoryFavorite.profile.name"
              :subtitle="[activeArmoryFavorite.profile.race, activeArmoryFavorite.profile.class].filter(Boolean).join(' · ')"
              :portrait="activeArmoryFavorite.profile.thumbnail_url"
              :emblem="classIcon(activeArmoryFavorite.profile.class)"
              emblem-icon
              :accent="classColor(activeArmoryFavorite.profile.class)"
              :metrics="gearShowcaseMetrics(activeArmoryFavorite.profile)"
            />

            <div class="armory-column armory-left">
              <a
                v-for="slot in leftGearSlots"
                :key="slot"
                class="armory-item"
                :class="{ 'is-empty': !gearItemBySlot(activeArmoryFavorite.profile, slot) }"
                :href="wowheadItemUrl(gearItemBySlot(activeArmoryFavorite.profile, slot))"
                :data-wowhead="wowheadItemData(gearItemBySlot(activeArmoryFavorite.profile, slot))"
                target="_blank"
                rel="noreferrer"
                :title="gearItemName(activeArmoryFavorite.profile, slot)"
              >
                <ItemIcon :item="gearItemBySlot(activeArmoryFavorite.profile, slot)" :alt="gearItemName(activeArmoryFavorite.profile, slot)" loading="eager" />
                <span>
                  <strong>{{ gearItemName(activeArmoryFavorite.profile, slot) }}</strong>
                  <small>{{ gearItemLevel(activeArmoryFavorite.profile, slot) }}</small>
                </span>
              </a>
            </div>

            <div class="armory-column armory-right">
              <a
                v-for="slot in rightGearSlots"
                :key="slot"
                class="armory-item is-right"
                :class="{ 'is-empty': !gearItemBySlot(activeArmoryFavorite.profile, slot) }"
                :href="wowheadItemUrl(gearItemBySlot(activeArmoryFavorite.profile, slot))"
                :data-wowhead="wowheadItemData(gearItemBySlot(activeArmoryFavorite.profile, slot))"
                target="_blank"
                rel="noreferrer"
                :title="gearItemName(activeArmoryFavorite.profile, slot)"
              >
                <span>
                  <strong>{{ gearItemName(activeArmoryFavorite.profile, slot) }}</strong>
                  <small>{{ gearItemLevel(activeArmoryFavorite.profile, slot) }}</small>
                </span>
                <ItemIcon :item="gearItemBySlot(activeArmoryFavorite.profile, slot)" :alt="gearItemName(activeArmoryFavorite.profile, slot)" loading="eager" />
              </a>
            </div>

            <div class="armory-weapons">
              <a
                v-for="slot in weaponGearSlots"
                :key="slot"
                class="armory-item armory-weapon"
                :class="{ 'is-empty': !gearItemBySlot(activeArmoryFavorite.profile, slot) }"
                :href="wowheadItemUrl(gearItemBySlot(activeArmoryFavorite.profile, slot))"
                :data-wowhead="wowheadItemData(gearItemBySlot(activeArmoryFavorite.profile, slot))"
                target="_blank"
                rel="noreferrer"
                :title="gearItemName(activeArmoryFavorite.profile, slot)"
              >
                <ItemIcon :item="gearItemBySlot(activeArmoryFavorite.profile, slot)" :alt="gearItemName(activeArmoryFavorite.profile, slot)" loading="eager" />
                <span>
                  <strong>{{ gearItemName(activeArmoryFavorite.profile, slot) }}</strong>
                  <small>{{ gearItemLevel(activeArmoryFavorite.profile, slot) }}</small>
                </span>
              </a>
            </div>
          </section>
        </div>

        <div v-else-if="armoryTab === 'talents'" class="armory-modal-body is-talents">
          <section v-if="talentLoading" class="talents-placeholder">
            <i class="fa-solid fa-spinner"></i>
            <span>Loading talents...</span>
          </section>

          <section v-else-if="talentEmbedUrl(activeArmoryFavorite.profile) && talentsFrameReady" class="talents-frame">
            <iframe
              :src="talentEmbedUrl(activeArmoryFavorite.profile)"
              title="Wowhead talent calculator"
              loading="eager"
              scrolling="no"
              referrerpolicy="no-referrer-when-downgrade"
              sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            ></iframe>
          </section>

          <section v-else-if="!talentEmbedUrl(activeArmoryFavorite.profile)" class="talents-placeholder">
            <i class="fa-solid fa-circle-info"></i>
            <span>Talent loadout is not available for this character yet.</span>
          </section>

          <section v-else class="talents-placeholder">
            <i class="fa-solid fa-spinner"></i>
            <span>Preparing the talent calculator...</span>
          </section>

          <a
            v-if="talentCalculatorUrl(activeArmoryFavorite.profile)"
            class="btn-sm-outline"
            :href="talentCalculatorUrl(activeArmoryFavorite.profile)"
            target="_blank"
            rel="noreferrer"
          >
            <i class="fa-solid fa-arrow-up-right-from-square"></i> Open Wowhead
          </a>
        </div>

        <footer class="armory-modal-foot">
          <span v-if="armoryPayload">
            <i class="fa-solid fa-clock-rotate-left"></i>
            Armory data from {{ formatArmoryTimestamp(armoryPayload.fetchedAt) }}
          </span>
          <span v-else>
            <i class="fa-solid fa-clock-rotate-left"></i>
            Raider.io data from {{ formatArmoryTimestamp(favoriteUpdatedAt(activeArmoryFavorite)) }}
          </span>
          <span v-if="isArmoryPayloadStale" class="armory-stale-badge" title="The armory did not answer, so an older copy is shown">
            <i class="fa-solid fa-triangle-exclamation"></i> Outdated
          </span>
          <a :href="worldOfWarcraftUrl(activeArmoryFavorite.profile)" target="_blank" rel="noreferrer">
            <i class="fa-solid fa-arrow-up-right-from-square"></i> Open on armory
          </a>
        </footer>
      </section>
    </div>
  </section>
</template>

<script>
import allianceBanner from '../assets/images/alliance-banner.svg'
import allianceHordeBanner from '../assets/images/alliance-horde-banner.svg'
import hordeBanner from '../assets/images/horde-banner.svg'
import rioServiceIcon from '../assets/images/services/rio-small-logo.svg'
import warcraftLogsServiceIcon from '../assets/images/services/warcraftlogs-small-logo.png'
import wowServiceIcon from '../assets/images/services/wow-small-logo.svg'
import deathKnightIcon from '../assets/images/classes/death knight.png'
import demonHunterIcon from '../assets/images/classes/demon hunter.png'
import druidIcon from '../assets/images/classes/druid.png'
import evokerIcon from '../assets/images/classes/evoker.png'
import hunterIcon from '../assets/images/classes/hunter.png'
import mageIcon from '../assets/images/classes/mage.png'
import monkIcon from '../assets/images/classes/monk.png'
import paladinIcon from '../assets/images/classes/paladin.png'
import priestIcon from '../assets/images/classes/priest.png'
import rogueIcon from '../assets/images/classes/rogue.png'
import shamanIcon from '../assets/images/classes/shaman.png'
import warlockIcon from '../assets/images/classes/warlock.png'
import warriorIcon from '../assets/images/classes/warrior.png'
import AppEmptyState from '../components/AppEmptyState.vue'
import ArmoryProfilePanel from '../components/ArmoryProfilePanel.vue'
import GearLoadoutShowcase from '../components/GearLoadoutShowcase.vue'
import ItemIcon from '../components/ItemIcon.vue'
import { getArmorySection, isArmoryDataStale } from '../services/armoryProfile'
import { DYNAMIC_DATA_TTL_MS, getCharacterProfile, getMythicPlusStaticData } from '../services/raiderApi'
import { encodedCharacterPath } from '../utils/characterSlug'
import { getBestMythicScore, parseCharacterUrl, summarizeRaidProgress } from '../utils/formatters'
import { ensureWowheadTooltips, refreshWowheadTooltips } from '../utils/wowheadTooltips'

const CDN_BASE_URL = 'https://cdn.raiderio.net'

const CLASS_ICONS = {
  'death knight': deathKnightIcon,
  deathknight: deathKnightIcon,
  'demon hunter': demonHunterIcon,
  demonhunter: demonHunterIcon,
  druid: druidIcon,
  evoker: evokerIcon,
  hunter: hunterIcon,
  mage: mageIcon,
  monk: monkIcon,
  paladin: paladinIcon,
  priest: priestIcon,
  rogue: rogueIcon,
  shaman: shamanIcon,
  warlock: warlockIcon,
  warrior: warriorIcon,
}

const CLASS_COLORS = {
  'death knight': '#c41e3a',
  deathknight: '#c41e3a',
  'demon hunter': '#a330c9',
  demonhunter: '#a330c9',
  druid: '#ff7c0a',
  evoker: '#33937f',
  hunter: '#aad372',
  mage: '#3fc7eb',
  monk: '#00ff98',
  paladin: '#f48cba',
  priest: '#ffffff',
  rogue: '#fff468',
  shaman: '#0070dd',
  warlock: '#8788ee',
  warrior: '#c69b6d',
}

const FACTION_ICONS = {
  alliance: allianceBanner,
  horde: hordeBanner,
  mixed: allianceHordeBanner,
}

const DEFAULT_FAVORITE_FILTERS = {
  search: '',
  sortBy: 'addedAt',
  viewMode: 'grid',
}
const FAVORITES_FILTERS_KEY = 'rio-dashboard-favorites-filters'
const FAVORITE_AUTO_REFRESH_AGE_MS = DYNAMIC_DATA_TTL_MS
// One dialog holds everything about a character. Tabs with a `section` are served by the armory
// worker through ArmoryProfilePanel; the rest render from the Raider.io profile we already have.
const ARMORY_MODAL_TABS = [
  { key: 'overview', section: 'profile', label: 'Overview', icon: 'fa-solid fa-chart-simple', eyebrow: 'World of Warcraft armory' },
  { key: 'gear', section: '', label: 'Gear', icon: 'fa-solid fa-shirt', eyebrow: 'Character gear' },
  { key: 'talents', section: '', label: 'Talents', icon: 'fa-solid fa-code-branch', eyebrow: 'Wowhead talent calculator' },
  { key: 'runs', section: '', label: 'M+ Runs', icon: 'fa-solid fa-gauge-high', eyebrow: 'Mythic Plus runs' },
  { key: 'reputation', section: 'reputation', label: 'Reputation', icon: 'fa-solid fa-handshake', eyebrow: 'Reputation' },
  { key: 'achievements', section: 'achievements', label: 'Achievements', icon: 'fa-solid fa-trophy', eyebrow: 'Achievements' },
]

function loadFavoriteFilters() {
  try {
    const saved = JSON.parse(localStorage.getItem(FAVORITES_FILTERS_KEY) || '{}')
    return {
      search: typeof saved.search === 'string' ? saved.search : DEFAULT_FAVORITE_FILTERS.search,
      sortBy: ['addedAt', 'score', 'itemLevel', 'name'].includes(saved.sortBy) ? saved.sortBy : DEFAULT_FAVORITE_FILTERS.sortBy,
      viewMode: ['grid', 'list'].includes(saved.viewMode) ? saved.viewMode : DEFAULT_FAVORITE_FILTERS.viewMode,
    }
  } catch (error) {
    return { ...DEFAULT_FAVORITE_FILTERS }
  }
}

export default {
  name: 'FavoritesView',
  components: {
    AppEmptyState,
    ArmoryProfilePanel,
    GearLoadoutShowcase,
    ItemIcon,
  },
  data() {
    const savedFilters = loadFavoriteFilters()

    return {
      characterUrl: '',
      search: savedFilters.search,
      sortBy: savedFilters.sortBy,
      viewMode: savedFilters.viewMode,
      draftSearch: savedFilters.search,
      draftSortBy: savedFilters.sortBy,
      message: '',
      loading: false,
      refreshingAll: false,
      autoRefreshChecked: false,
      isFiltersOpen: false,
      closingFilters: false,
      runsMode: 'best',
      armoryTabs: ARMORY_MODAL_TABS,
      armoryTab: 'overview',
      activeArmoryFavorite: null,
      armoryRender: null,
      armoryPayload: null,
      talentsFrameReady: false,
      talentsFrameTimer: null,
      closingArmory: false,
      talentLoading: false,
      activeMenuFavoriteId: null,
      dungeons: [],
      leftGearSlots: ['head', 'neck', 'shoulder', 'back', 'chest', 'shirt', 'tabard', 'wrist'],
      rightGearSlots: ['hands', 'waist', 'legs', 'feet', 'finger1', 'finger2', 'trinket1', 'trinket2'],
      weaponGearSlots: ['mainhand', 'offhand'],
    }
  },
  computed: {
    favorites() {
      return this.$store.getters['getFavorites']
    },
    isDarkMode() {
      return this.$store.getters['getThemeMode'] === 'dark'
    },
    sortedFavorites() {
      const needle = this.search.toLowerCase()

      return [...this.favorites]
        .filter((item) => {
          if (!needle) {
            return true
          }
          const text = [item.profile.name, item.profile.class, item.profile.realm, item.profile.guild?.name]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
          return text.includes(needle)
        })
        .sort((left, right) => {
          if (this.sortBy === 'score') {
            return getBestMythicScore(right.profile) - getBestMythicScore(left.profile)
          }
          if (this.sortBy === 'itemLevel') {
            return (right.profile.gear?.item_level_equipped || 0) - (left.profile.gear?.item_level_equipped || 0)
          }
          if (this.sortBy === 'name') {
            return left.profile.name.localeCompare(right.profile.name, 'ru')
          }
          return new Date(right.addedAt).getTime() - new Date(left.addedAt).getTime()
        })
    },
    activeArmoryTab() {
      return ARMORY_MODAL_TABS.find((tab) => tab.key === this.armoryTab) || ARMORY_MODAL_TABS[0]
    },
    isArmoryPayloadStale() {
      return isArmoryDataStale(this.armoryPayload)
    },
  },
  mounted() {
    this.ensureWowheadTooltips()
    this.loadDungeonIcons()
    this.refreshStaleFavorites()
    document.addEventListener('click', this.closeFavoriteMenuOnOutsideClick)
    window.addEventListener('keydown', this.closeOpenDialogOnEscape)
  },
  watch: {
    search() {
      this.persistFavoriteFilters()
    },
    sortBy() {
      this.persistFavoriteFilters()
    },
    viewMode() {
      this.persistFavoriteFilters()
      // Wowhead rebinds tooltips to the DOM nodes it saw last, so the relaid-out icons need a pass.
      this.$nextTick(() => this.refreshWowheadTooltips())
    },
  },
  beforeUnmount() {
    window.clearTimeout(this.talentsFrameTimer)
    document.removeEventListener('click', this.closeFavoriteMenuOnOutsideClick)
    window.removeEventListener('keydown', this.closeOpenDialogOnEscape)
    document.body.classList.remove('is-scroll-locked')
  },
  methods: {
    addFavorite(character, source = 'manual', silent = false) {
      this.$store.dispatch('addFavorite', { character, source, silent })
    },
    removeFavorite(id) {
      const favorite = this.favorites.find((item) => item.id === id)
      this.$store.dispatch('requestConfirmation', {
        title: 'Remove favorite?',
        message: `Remove ${favorite?.profile?.name || 'this character'} from favorites and compare?`,
        confirmLabel: 'Remove',
        action: 'removeFavorite',
        payload: id,
      })
    },
    addToCompare(favorite) {
      this.$store.dispatch('addToCompare', favorite)
    },
    toggleFavoriteMenu(id) {
      this.activeMenuFavoriteId = this.activeMenuFavoriteId === id ? null : id
    },
    closeFavoriteMenuOnOutsideClick() {
      this.activeMenuFavoriteId = null
    },
    addToCompareFromMenu(favorite) {
      this.addToCompare(favorite)
      this.activeMenuFavoriteId = null
    },
    removeFavoriteFromMenu(id) {
      this.removeFavorite(id)
      this.activeMenuFavoriteId = null
    },
    refreshFavoriteFromMenu(favorite) {
      this.activeMenuFavoriteId = null
      this.refreshFavorite(favorite)
    },
    favoriteUpdatedAt(favorite) {
      return favorite.refreshedAt || favorite.updatedAt || favorite.addedAt || favorite.profile?.last_crawled_at || ''
    },
    isFavoriteStale(favorite) {
      const updatedAt = this.favoriteUpdatedAt(favorite)
      const timestamp = updatedAt ? new Date(updatedAt).getTime() : 0
      if (!timestamp || Number.isNaN(timestamp)) {
        return true
      }
      return Date.now() - timestamp > FAVORITE_AUTO_REFRESH_AGE_MS
    },
    refreshStaleFavorites() {
      if (this.autoRefreshChecked || !this.favorites.length) return
      this.autoRefreshChecked = true

      const staleFavorites = this.favorites.filter((favorite) => this.isFavoriteStale(favorite))
      if (!staleFavorites.length) return

      this.refreshAllFavorites(true, staleFavorites)
    },
    openFilters() {
      this.draftSearch = this.search
      this.draftSortBy = this.sortBy
      this.closingFilters = false
      this.isFiltersOpen = true
      document.body.classList.add('is-scroll-locked')
    },
    closeFilters() {
      if (!this.isFiltersOpen || this.closingFilters) return
      this.closingFilters = true
      window.setTimeout(() => {
        this.isFiltersOpen = false
        this.closingFilters = false
        document.body.classList.remove('is-scroll-locked')
      }, 180)
    },
    persistFavoriteFilters() {
      try {
        localStorage.setItem(FAVORITES_FILTERS_KEY, JSON.stringify({
          search: this.search,
          sortBy: this.sortBy,
          viewMode: this.viewMode,
        }))
      } catch (error) {
        console.warn('Unable to save favorite filters', error)
      }
    },
    applyFilters() {
      this.search = this.draftSearch
      this.sortBy = this.draftSortBy
      this.closeFilters()
    },
    resetFilters() {
      this.search = DEFAULT_FAVORITE_FILTERS.search
      this.sortBy = DEFAULT_FAVORITE_FILTERS.sortBy
      this.draftSearch = DEFAULT_FAVORITE_FILTERS.search
      this.draftSortBy = DEFAULT_FAVORITE_FILTERS.sortBy
      this.closeFilters()
    },
    openArmory(favorite) {
      this.closingArmory = false
      this.armoryTab = 'overview'
      this.runsMode = 'best'
      this.activeArmoryFavorite = favorite
      this.armoryRender = null
      this.talentsFrameReady = false
      this.loadArmoryRender(favorite)
      this.lockPageScroll()
    },
    // The armory ships the same three-layer character shot it uses on its own profile page; the
    // gear tab reuses it as a backdrop instead of the plain avatar circle.
    async loadArmoryRender(favorite) {
      try {
        const payload = await getArmorySection('profile', favorite.profile)
        if (this.activeArmoryFavorite?.id === favorite.id) {
          this.armoryRender = payload?.character?.render || null
          this.armoryPayload = payload
        }
      } catch (error) {
        this.armoryRender = null
      }
    },
    formatArmoryTimestamp(value) {
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? 'an unknown time' : date.toLocaleString()
    },
    closeArmory() {
      if (!this.activeArmoryFavorite || this.closingArmory) return
      this.closingArmory = true
      window.setTimeout(() => {
        this.activeArmoryFavorite = null
        this.closingArmory = false
        this.talentLoading = false
        this.releaseModalScroll()
      }, 190)
    },
    selectArmoryTab(tab) {
      this.armoryTab = tab

      if (tab === 'gear') {
        this.$nextTick(() => this.refreshWowheadTooltips())
        return
      }

      if (tab === 'talents') {
        this.ensureTalentLoadout().then(() => this.mountTalentsFrame())
      }
    },
    // The Wowhead embed measures its container on mount and bails out if the layout has not
    // settled yet ("Could not configure the responsive row"), so the iframe is inserted only
    // after the tab has been laid out. A timer rather than requestAnimationFrame: rAF does not
    // fire in a background tab, which would leave the calculator permanently unmounted.
    mountTalentsFrame() {
      this.talentsFrameReady = false
      window.clearTimeout(this.talentsFrameTimer)
      this.$nextTick(() => {
        this.talentsFrameTimer = window.setTimeout(() => {
          if (this.armoryTab === 'talents') this.talentsFrameReady = true
        }, 60)
      })
    },
    // The favorites list is fetched without the talent loadout, so the tab pulls a full profile
    // the first time it is opened.
    async ensureTalentLoadout() {
      const favorite = this.activeArmoryFavorite
      if (!favorite || this.talentLoading || this.talentLoadoutText(favorite.profile)) return

      this.talentLoading = true
      const loadingId = await this.$store.dispatch('startGlobalLoading', 'Loading talents...')
      try {
        const profile = await this.fetchProfile({
          region: favorite.profile.region,
          realm: favorite.profile.realm,
          name: favorite.profile.name,
        })
        if (this.activeArmoryFavorite?.id === favorite.id) {
          this.activeArmoryFavorite = { ...favorite, profile }
        }
      } catch (error) {
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Talents unavailable',
          description: `Unable to load talents for ${favorite.profile.name}.`,
        })
      } finally {
        this.talentLoading = false
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    lockPageScroll() {
      document.body.classList.add('is-scroll-locked')
    },
    releaseModalScroll() {
      if (this.activeArmoryFavorite) {
        return
      }
      document.body.classList.remove('is-scroll-locked')
    },
    closeOpenDialogOnEscape(event) {
      if (event.key !== 'Escape') return

      if (this.activeArmoryFavorite) {
        this.closeArmory()
        return
      }

      if (this.isFiltersOpen) {
        this.closeFilters()
        return
      }

      this.activeMenuFavoriteId = null
    },
    async loadDungeonIcons() {
      const loadingId = await this.$store.dispatch('startGlobalLoading', {
        id: 'page:favorites-dungeons',
        message: 'Loading dungeon data...',
      })
      try {
        const results = await Promise.allSettled([11, 10, 9, 8].map((expansionId) => getMythicPlusStaticData(expansionId)))
        this.dungeons = results
          .filter((result) => result.status === 'fulfilled')
          .flatMap((result) => result.value.dungeons || [])
      } catch (error) {
        this.dungeons = []
      } finally {
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    showComingSoon() {
      this.$store.dispatch('notify', {
        type: 'warning',
        title: 'Coming soon',
        description: 'Talents view is not available yet.',
      })
    },
    async addByUrl() {
      if (!this.characterUrl) {
        this.message = 'Paste a character URL.'
        this.$store.dispatch('notify', {
          type: 'warning',
          title: 'Character URL required',
          description: 'Paste a Raider.io character URL before adding a favorite.',
        })
        return
      }

      this.loading = true
      this.message = ''
      const loadingId = await this.$store.dispatch('startGlobalLoading', 'Loading character profile...')

      try {
        const parsed = parseCharacterUrl(this.characterUrl)
        const profile = await this.fetchProfile(parsed, { force: true })
        this.addFavorite(profile, 'manual')
        this.characterUrl = ''
        this.message = `${profile.name} has been added to favorites.`
      } catch (error) {
        this.message = error.message || 'Unable to add character.'
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Unable to add character',
          description: this.message,
        })
      } finally {
        this.loading = false
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    async refreshFavorite(favorite) {
      this.loading = true
      this.message = ''
      const loadingId = await this.$store.dispatch('startGlobalLoading', 'Refreshing character profile...')

      try {
        const profile = await this.fetchProfile({
          region: favorite.profile.region,
          realm: favorite.profile.realm,
          name: favorite.profile.name,
        }, { force: true })
        this.addFavorite(profile, favorite.source, true)
        this.message = `${profile.name} profile has been refreshed.`
        this.$store.dispatch('notify', {
          type: 'success',
          title: 'Favorite refreshed',
          description: `${profile.name} profile has been refreshed.`,
        })
      } catch (error) {
        this.message = `Unable to refresh ${favorite.profile.name}.`
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Refresh failed',
          description: this.message,
        })
      } finally {
        this.loading = false
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    async refreshAllFavorites(isAutomatic = false, favorites = this.favorites) {
      const targets = favorites.filter(Boolean)
      if (!targets.length || this.refreshingAll) return

      this.refreshingAll = true
      this.loading = true
      this.message = ''
      const loadingId = await this.$store.dispatch(
        'startGlobalLoading',
        isAutomatic ? 'Refreshing stale character profiles...' : 'Refreshing all character profiles...'
      )

      const failedNames = []
      let refreshedCount = 0

      try {
        for (const favorite of targets) {
          try {
            const profile = await this.fetchProfile({
              region: favorite.profile.region,
              realm: favorite.profile.realm,
              name: favorite.profile.name,
            }, { force: true })
            this.addFavorite(profile, favorite.source, true)
            refreshedCount += 1
          } catch (error) {
            failedNames.push(favorite.profile?.name || 'Unknown')
          }
        }

        if (refreshedCount || failedNames.length) {
          this.$store.dispatch('notify', {
            type: failedNames.length ? 'warning' : 'success',
            title: isAutomatic ? 'Favorites auto-refreshed' : 'Favorites refreshed',
            description: failedNames.length
              ? `${refreshedCount}/${targets.length} refreshed. Failed: ${failedNames.slice(0, 3).join(', ')}${failedNames.length > 3 ? '...' : ''}.`
              : `${refreshedCount} character profile${refreshedCount === 1 ? '' : 's'} refreshed.`,
          })
        }
      } finally {
        this.refreshingAll = false
        this.loading = false
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    fetchProfile(parsed, options = {}) {
      return getCharacterProfile({
        region: parsed.region,
        realm: parsed.realm,
        name: parsed.name,
        fields: [
          'gear',
          'talents',
          'guild',
          'raid_progression:current-expansion',
          'mythic_plus_scores_by_season:current',
          'mythic_plus_best_runs:all',
          'mythic_plus_recent_runs',
          'mythic_plus_ranks',
          'realm',
        ].join(','),
      }, options)
    },
    ensureWowheadTooltips() {
      ensureWowheadTooltips()
    },
    refreshWowheadTooltips() {
      this.$nextTick(() => {
        refreshWowheadTooltips()
      })
    },
    bestScore(profile) {
      const score = getBestMythicScore(profile)
      return score ? Math.round(score).toString() : '-'
    },
    itemLevel(profile) {
      const level = profile.gear?.item_level_equipped
      return level ? Math.round(Number(level)).toString() : '-'
    },
    mythicPlusSpecRank(profile, scope) {
      const ranks = profile.mythic_plus_ranks || {}
      const role = String(profile.active_spec_role || '').toLowerCase()
      const spec = String(profile.active_spec_name || '').toLowerCase().replace(/[\s-]+/g, '_')
      const characterClass = String(profile.class || '').toLowerCase().replace(/[\s-]+/g, '_')
      const candidates = [
        spec && `spec_${spec}`,
        spec,
        characterClass && spec && `${characterClass}_${spec}`,
        role && `class_${role}`,
        role,
        'class',
        'overall',
      ].filter(Boolean)

      const rankGroup = candidates.map((key) => ranks[key]).find((item) => item?.[scope])
      return rankGroup ? String(rankGroup[scope]) : '-'
    },
    raidProgressLabel(profile) {
      return summarizeRaidProgress(profile)[0]?.summary || '-'
    },
    talentLoadoutText(profile) {
      return profile.talentLoadout?.loadout_text || profile.talent_loadout?.loadout_text || profile.talents?.loadout_text || ''
    },
    talentCalculatorUrl(profile) {
      const loadoutText = this.talentLoadoutText(profile)
      return loadoutText ? `https://www.wowhead.com/talent-calc/blizzard/${encodeURIComponent(loadoutText)}` : ''
    },
    talentEmbedUrl(profile) {
      const loadoutText = this.talentLoadoutText(profile)
      return loadoutText ? `https://www.wowhead.com/talent-calc/embed/blizzard/${encodeURIComponent(loadoutText)}` : ''
    },
    factionKey(faction) {
      const key = String(faction || '').toLowerCase()
      return key === 'alliance' || key === 'horde' ? key : 'mixed'
    },
    factionIcon(faction) {
      return FACTION_ICONS[this.factionKey(faction)]
    },
    serviceLinks(profile) {
      return [
        {
          key: 'warcraftlogs',
          label: 'Warcraft Logs',
          icon: warcraftLogsServiceIcon,
          href: this.warcraftLogsUrl(profile),
        },
        {
          key: 'raiderio',
          label: 'Raider.io',
          icon: rioServiceIcon,
          href: profile.profile_url || this.raiderIoUrl(profile),
        },
        {
          key: 'worldofwarcraft',
          label: 'World of Warcraft',
          icon: wowServiceIcon,
          href: this.worldOfWarcraftUrl(profile),
        },
      ]
    },
    externalUrl(profile, keys) {
      const directUrl = keys.map((key) => profile[key]).find(Boolean)
      if (directUrl) return directUrl

      const linkSources = [profile.links, profile.external_links, profile.externalLinks].filter(Boolean)
      for (const source of linkSources) {
        if (Array.isArray(source)) {
          const match = source.find((item) => {
            const haystack = [item.key, item.type, item.name, item.label, item.slug].filter(Boolean).join(' ').toLowerCase()
            return keys.some((key) => haystack.includes(key.replace(/_/g, '')))
          })
          if (match?.url || match?.href) return match.url || match.href
          continue
        }

        const nestedUrl = keys.map((key) => source[key]?.url || source[key]?.href || source[key]).find(Boolean)
        if (nestedUrl) return nestedUrl
      }

      return ''
    },
    encodedCharacterPath(profile) {
      return encodedCharacterPath(profile)
    },
    raiderIoUrl(profile) {
      const path = this.encodedCharacterPath(profile)
      return `https://raider.io/characters/${path.region}/${path.realm}/${path.name}`
    },
    warcraftLogsUrl(profile) {
      return this.externalUrl(profile, [
        'warcraftlogs_url',
        'warcraft_logs_url',
        'warcraftLogsUrl',
        'wcl_url',
      ]) || (() => {
        const path = this.encodedCharacterPath(profile)
        return `https://www.warcraftlogs.com/character/${path.region}/${path.realm}/${path.name}`
      })()
    },
    worldOfWarcraftUrl(profile) {
      return this.externalUrl(profile, [
        'worldofwarcraft_url',
        'world_of_warcraft_url',
        'wow_profile_url',
        'armory_url',
        'blizzard_url',
      ]) || (() => {
        const path = this.encodedCharacterPath(profile)
        const locale = path.region === 'eu' ? 'en-gb' : 'en-us'
        return `https://worldofwarcraft.blizzard.com/${locale}/character/${path.region}/${path.realm}/${path.name}`
      })()
    },
    classIcon(characterClass) {
      const key = String(characterClass || '').toLowerCase()
      return CLASS_ICONS[key.replace(/-/g, ' ')] || CLASS_ICONS[key.replace(/\s/g, '')] || ''
    },
    favoriteNameStyle(characterClass) {
      if (!this.isDarkMode) {
        return null
      }

      const key = String(characterClass || '').toLowerCase()
      const color = CLASS_COLORS[key.replace(/-/g, ' ')] || CLASS_COLORS[key.replace(/\s/g, '')]
      return color ? { color } : null
    },
    classColor(characterClass) {
      const key = String(characterClass || '').toLowerCase()
      return CLASS_COLORS[key.replace(/-/g, ' ')] || CLASS_COLORS[key.replace(/\s/g, '')] || 'var(--primary)'
    },
    gearShowcaseMetrics(profile) {
      const slots = [...this.leftGearSlots, ...this.rightGearSlots, ...this.weaponGearSlots]
      const equipped = slots.filter((slot) => this.gearItemBySlot(profile, slot)).length

      return [
        { label: 'Item level', value: this.itemLevel(profile) },
        { label: 'Equipped', value: `${equipped}/${slots.length}` },
      ]
    },
    bestRuns(profile) {
      return profile.mythic_plus_best_runs || []
    },
    recentRuns(profile) {
      return (profile.mythic_plus_recent_runs || []).slice(0, 15)
    },
    // Colour the keystone badge by bracket, the way the ladder does: the number alone does not
    // tell you whether a run was notable.
    keystoneTierClass(run) {
      const level = Number(run.mythic_level) || 0
      if (level >= 20) return 'is-legendary'
      if (level >= 15) return 'is-epic'
      if (level >= 10) return 'is-rare'
      return 'is-common'
    },
    runCompletedAt(run) {
      const date = this.runCompletedDate(run)
      return date ? date.toLocaleDateString() : '-'
    },
    // The run cards give this a quarter of their width, so the full locale date does not fit.
    runCompletedShort(run) {
      const date = this.runCompletedDate(run)
      return date ? date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '-'
    },
    runCompletedDate(run) {
      const value = run.completed_at || run.completedAt
      if (!value) return null
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? null : date
    },
    formatScore(score) {
      return Number(score || 0).toFixed(1)
    },
    runTime(run) {
      const value = run.clear_time_ms || run.time_ms
      if (!value) return '-'
      const totalSeconds = Math.floor(value / 1000)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      return `${minutes}:${String(seconds).padStart(2, '0')}`
    },
    upgradeStars(run) {
      const upgrades = run.num_keystone_upgrades || 0
      return upgrades ? '+'.repeat(upgrades) : '-'
    },
    normalizeAssetUrl(url) {
      if (!url) return ''
      return url.startsWith('http') ? url : `${CDN_BASE_URL}${url}`
    },
    dungeonRunName(run) {
      return typeof run.dungeon === 'string' ? run.dungeon : run.dungeon?.name || '-'
    },
    dungeonStaticData(run) {
      const dungeonName = this.dungeonRunName(run).toLowerCase()
      const dungeonSlug = String(run.dungeon_slug || run.dungeon?.slug || run.short_name || '')
        .toLowerCase()
        .replace(/\s/g, '-')
      return this.dungeons.find((item) => {
        return item.slug === dungeonSlug || String(item.name || '').toLowerCase() === dungeonName
      })
    },
    dungeonRunIcon(run) {
      const directIcon = run.dungeon_icon_url || run.dungeon?.icon_url || run.icon_url
      if (directIcon) return this.normalizeAssetUrl(directIcon)

      const dungeon = this.dungeonStaticData(run)
      return this.normalizeAssetUrl(dungeon?.icon_url || dungeon?.background_image_url)
    },
    dungeonRunBanner(run) {
      const dungeon = this.dungeonStaticData(run)
      return this.normalizeAssetUrl(dungeon?.background_image_url || run.background_image_url || '')
    },
    chestLabel(run) {
      const upgrades = run.num_keystone_upgrades || 0
      if (!upgrades) return 'depleted'
      return upgrades === 1 ? 'chest' : 'chests'
    },
    // How much of the keystone timer was left: that is what separates a strong run from one that
    // barely made it, and it is more useful than the raw clear time on its own.
    timeRemainingLabel(run) {
      const limit = this.dungeonStaticData(run)?.keystone_timer_seconds
      const clearMs = run.clear_time_ms || run.time_ms
      if (!limit || !clearMs) return 'clear time'

      const diffSeconds = Math.round(limit - clearMs / 1000)
      if (diffSeconds <= 0) return 'over time'

      const minutes = Math.floor(diffSeconds / 60)
      const seconds = diffSeconds % 60
      return `${minutes}:${String(seconds).padStart(2, '0')} under`
    },
    normalizedGearItems(profile) {
      const items = profile.gear?.items
      if (!items) return []

      if (Array.isArray(items)) {
        return items.map((item, index) => this.normalizeGearItem(item, item.slot || item.slot_key || `${index}`))
      }

      return Object.entries(items).map(([slotKey, item]) => this.normalizeGearItem(item, slotKey))
    },
    normalizeGearItem(item, slotKey) {
      return {
        ...(item || {}),
        slotKey: this.normalizeSlotKey(slotKey || item?.slot || item?.inventory_type?.type),
      }
    },
    normalizeSlotKey(slot) {
      return String(slot || '')
        .toLowerCase()
        .replace(/_/g, '')
        .replace(/-/g, '')
        .replace(/\s/g, '')
    },
    gearItemBySlot(profile, slot) {
      const normalizedSlot = this.normalizeSlotKey(slot)
      return this.normalizedGearItems(profile).find((item) => item.slotKey === normalizedSlot)
    },
    weaponItem(profile) {
      return this.weaponItems(profile)[0]
    },
    weaponItems(profile) {
      return ['mainhand', 'offhand']
        .map((slot) => this.gearItemBySlot(profile, slot))
        .filter(Boolean)
    },
    trinketItems(profile) {
      return ['trinket1', 'trinket2']
        .map((slot) => this.gearItemBySlot(profile, slot))
        .filter(Boolean)
    },
    trinketSlots(profile) {
      return ['trinket1', 'trinket2'].map((slot) => {
        return this.gearItemBySlot(profile, slot) || {
          slotKey: slot,
          name: this.slotLabel(slot),
        }
      })
    },
    // Weapons and trinkets are the slots people actually glance at; the full set lives behind the
    // Gear tab of the armory dialog.
    keyGearItems(profile) {
      return [...this.weaponItems(profile), ...this.trinketSlots(profile)]
    },
    keyGearItemLevel(item) {
      return item?.item_level || item?.itemLevel || ''
    },
    rankLabel(profile, scope) {
      const rank = this.mythicPlusSpecRank(profile, scope)
      return rank === '-' ? rank : `#${Number(rank).toLocaleString('en-US')}`
    },
    specLine(profile) {
      return [
        [profile.active_spec_name, profile.class].filter(Boolean).join(' '),
        profile.race,
      ].filter(Boolean).join(' · ')
    },
    gearItemName(profile, slot) {
      return this.gearItemBySlot(profile, slot)?.name || this.slotLabel(slot)
    },
    gearItemLevel(profile, slot) {
      const item = this.gearItemBySlot(profile, slot)
      return item?.item_level || item?.itemLevel || '-'
    },
    slotLabel(slot) {
      const labels = {
        head: 'Head',
        neck: 'Neck',
        shoulder: 'Shoulder',
        back: 'Back',
        chest: 'Chest',
        shirt: 'Shirt',
        tabard: 'Tabard',
        wrist: 'Wrist',
        hands: 'Hands',
        waist: 'Waist',
        legs: 'Legs',
        feet: 'Feet',
        finger1: 'Finger',
        finger2: 'Finger',
        trinket1: 'Trinket',
        trinket2: 'Trinket',
        mainhand: 'Main hand',
        offhand: 'Off hand',
      }
      return labels[slot] || 'Empty slot'
    },
    wowheadItemUrl(item) {
      const itemId = item?.item_id || item?.id
      return itemId ? `https://www.wowhead.com/item=${itemId}` : 'https://www.wowhead.com/'
    },
    wowheadItemData(item) {
      const itemId = item?.item_id || item?.id
      if (!itemId) return null

      const params = [`item=${itemId}`]
      const itemLevel = item.item_level || item.itemLevel
      if (itemLevel) params.push(`ilvl=${itemLevel}`)
      if (Array.isArray(item.bonuses) && item.bonuses.length) params.push(`bonus=${item.bonuses.join(':')}`)
      if (Array.isArray(item.gems) && item.gems.length) params.push(`gems=${item.gems.map((gem) => gem.item_id || gem.id || gem).join(':')}`)
      if (item.enchant?.id || item.enchant_id) params.push(`ench=${item.enchant?.id || item.enchant_id}`)
      return params.join('&')
    },
  },
}
</script>
